// Port of ios/Sources/Models/PostEmbed.swift.
//
// The one preview card a post is worth (shared/API.md §12.4) — the server's `metadata.embeds`
// reduced to the first entry that can actually be drawn. Reduced at DECODE time and stored as
// one JSON column on the post row (`PostRecord.embedJson`), because the row rides every existing
// upsert path unchanged and the server recomputes embeds on every payload, so the column can
// never go stale.
//
// Only two kinds earn a card, matching what the desktop actually shows:
// - `opengraph`: an external page that offered a title/description. A bare `link` embed draws
//   nothing on the desktop either — the URL is already in the message text.
// - `permalink`: another Mattermost message, quoted — what a forwarded message renders as.

export interface PostEmbed {
  type: string;
  url: string;
  title: string;
  description: string;
  /** OpenGraph `site_name`, falling back to the URL's host — the card's eyebrow line. */
  siteName: string;
  targetPostId: string;
  targetChannelId: string;
  channelDisplayName: string;
}

const typeOpengraph = 'opengraph';
const typePermalink = 'permalink';

function isDrawable(embed: Pick<PostEmbed, 'title' | 'description'>): boolean {
  return embed.title !== '' || embed.description !== '';
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A loosely-decoded optional string: anything that is not a string reads as absent. */
function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/**
 * The stored column back into a card, or null. Same contract as Swift's `Codable` decode: every
 * field must be present as a string (the encoder below always writes all eight), and a card
 * with no type or nothing to draw is no card.
 */
function fromJson(json: string): PostEmbed | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isObject(raw)) return null;
  const fields = [
    raw.type,
    raw.url,
    raw.title,
    raw.description,
    raw.site_name,
    raw.target_post_id,
    raw.target_channel_id,
    raw.channel_display_name,
  ];
  if (!fields.every((f) => typeof f === 'string')) return null;
  const embed: PostEmbed = {
    type: raw.type as string,
    url: raw.url as string,
    title: raw.title as string,
    description: raw.description as string,
    siteName: raw.site_name as string,
    targetPostId: raw.target_post_id as string,
    targetChannelId: raw.target_channel_id as string,
    channelDisplayName: raw.channel_display_name as string,
  };
  return embed.type === '' || !isDrawable(embed) ? null : embed;
}

/** The stored form: the same snake_case keys as the Swift `CodingKeys`. */
function encode(embed: PostEmbed): string {
  return JSON.stringify({
    type: embed.type,
    url: embed.url,
    title: embed.title,
    description: embed.description,
    site_name: embed.siteName,
    target_post_id: embed.targetPostId,
    target_channel_id: embed.targetChannelId,
    channel_display_name: embed.channelDisplayName,
  });
}

/** Swift's `.whitespaces` trim (spaces and tabs, not newlines). */
function isBlank(s: string): boolean {
  return s.replace(/[\p{Zs}\t]/gu, '') === '';
}

function hostOf(url: string): string {
  const at = url.indexOf('://');
  const afterScheme = at < 0 ? '' : url.slice(at + 3);
  return afterScheme.split('/')[0].split(':')[0];
}

/**
 * The first usable card in a post's `metadata` (the parsed JSON object the post payload
 * carries), or null when none of its embeds earns one. Walks `metadata.embeds` in the server's
 * order and keeps the FIRST usable entry — the desktop previews the first link too, and one card
 * per message is the ceiling anyway.
 *
 * Decoding is loose, like Swift's `EmbedDTO` (every field optional): a value of the wrong JSON
 * type reads as absent instead of failing the whole payload.
 */
function firstUsable(metadata: unknown): PostEmbed | null {
  if (!isObject(metadata) || !Array.isArray(metadata.embeds)) return null;
  for (const entry of metadata.embeds) {
    if (!isObject(entry)) continue;
    const data = isObject(entry.data) ? entry.data : null;
    switch (str(entry.type) ?? '') {
      case typeOpengraph: {
        if (!data) continue;
        const url = str(entry.url) ?? '';
        const title = str(data.title) ?? '';
        const description = str(data.description) ?? '';
        if (title === '' && description === '') continue;
        const siteName = str(data.site_name);
        return {
          type: typeOpengraph,
          url,
          title,
          description,
          siteName: siteName ? siteName : hostOf(url),
          targetPostId: '',
          targetChannelId: '',
          channelDisplayName: '',
        };
      }
      case typePermalink: {
        if (!data) continue;
        const targetPostId = str(data.post_id);
        const targetChannelId = str(data.channel_id);
        if (!targetPostId || !targetChannelId) continue;
        const snippet = (isObject(data.post) ? str(data.post.message) : null) ?? '';
        if (isBlank(snippet)) continue;
        return {
          type: typePermalink,
          url: '',
          title: '',
          description: snippet,
          siteName: '',
          targetPostId,
          targetChannelId,
          channelDisplayName: str(data.channel_display_name) ?? '',
        };
      }
      default:
        // `link` (no OpenGraph data) and `image` draw nothing on the desktop either.
        continue;
    }
  }
  return null;
}

/** The serialized card for a post payload's `metadata` — what goes in `PostRecord.embedJson`. */
function firstUsableJson(metadata: unknown): string | null {
  const embed = firstUsable(metadata);
  return embed ? encode(embed) : null;
}

export const PostEmbed = {
  typeOpengraph,
  typePermalink,
  isDrawable,
  fromJson,
  encode,
  firstUsable,
  firstUsableJson,
} as const;
